import { useCallback, useEffect, useRef, useState } from 'react';

import { request } from '../lib/api';
import type { DashboardSummary } from './portalTypes';

function useDashboardSummary() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const reload = useCallback(async (showLoading = true, signal?: AbortSignal) => {
    if (signal?.aborted) return;
    const version = ++requestVersion.current;
    const valid = () => version === requestVersion.current && !signal?.aborted;
    if (showLoading) {
      setLoading(true);
      setSummary(null);
    }
    setError(null);
    try {
      const data = await request<DashboardSummary>('/me/dashboard', signal ? { signal } : undefined);
      if (valid()) setSummary(data);
    } catch (err: any) {
      if (valid()) {
        setSummary(null);
        setError(err?.data?.message ?? 'Unable to load your account.');
      }
    } finally {
      if (valid()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    return () => { requestVersion.current += 1; };
  }, [reload]);

  return { summary, loading, error, reload };
}

export { useDashboardSummary };
